import {HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as moment from 'moment';
import 'rxjs/add/operator/finally';
import {ServerErrorBody} from '../services/server-error-code';
import {UserService} from '../services/user.service';
import {HTTP_STATUS_CODES} from '../shared.constants/all-constants';
import {
  CALENDAR_MONTH_OFFSET,
  DATE_DAY_PART_INDEX,
  DATE_FORMAT,
  DATE_MONTH_PART_INDEX,
  DATE_PART_SEPARATOR,
  DATE_YEAR_PART_INDEX,
  DISPLAY_DATE_SEPARATOR,
  EMPTY_TEXT,
  MINIMUM_CHANGE_DESCRIPTION_LENGTH,
  MODAL_RESULT_OK,
  NO_VACATION_EXTENSION_DAYS,
  VACATION_DAY_ADJUSTMENT,
  VACATION_DAYS_SIGN
} from './report-datepicker.constants';
import {applyReportDatepickerError} from './report-datepicker-error.state';
import {ReportDatepickerUiGateway} from './report-datepicker-ui.gateway';
import {
  CurrentVacation,
  ReportDatepickerError,
  ReportDatepickerVacationView,
  SuccessParams,
  VacationParams,
  VacationResponse
} from './report-datepicker.types';
import {VacationTypeCreation} from './vacation-type-creation';
import {VacationTypes} from './vacation-Types';
import {TIMEOUTS} from './const-timeout';
import {UnpaidLeavePreviewState} from './unpaid-leave-preview.state';

const STRING_TYPE = 'string';

@Injectable()
export class ReportDatepickerVacationCoordinator {
  readonly unpaidPreview: UnpaidLeavePreviewState;
  constructor(
    private userService: UserService,
    private translateService: TranslateService,
    private ui: ReportDatepickerUiGateway
  ) {
    this.unpaidPreview = new UnpaidLeavePreviewState(userService);
  }

  selectType(view: ReportDatepickerVacationView, value: string): void {
    view.type = Number(value);
    this.unpaidPreview.refresh(view);
  }

  cancelConfirmation(view: ReportDatepickerVacationView): void {
    view.showConfirmationScreen = false;
  }

  isSubmissionDisabled(view: ReportDatepickerVacationView): boolean {
    return view.changeLog.length < MINIMUM_CHANGE_DESCRIPTION_LENGTH ||
      (view.type === VacationTypes.UNPAID_LEAVE && !this.unpaidPreview.ready(view)) ||
      view.type === VacationTypes.NOT_CHOSEN ||
      view.suc || view.isWaiting || view.showConfirmationScreen;
  }

  submitRequest(view: ReportDatepickerVacationView): void {
    if (view.type === VacationTypes.UNPAID_LEAVE && !this.unpaidPreview.ready(view)) { return; }
    view.wrongError = view.wrongError409 = view.wrongError400 = false;
    view.currentVacations = [];
    view.vocationDays = NO_VACATION_EXTENSION_DAYS;
    const fromDate = new Date(
      view.from.year, view.from.month - CALENDAR_MONTH_OFFSET, view.from.day);
    const params = this.prepareRequestParameters(view, fromDate);
    this.userService.postVocation(params).subscribe(
      (response: HttpResponse<Object>) => this.processResponse(view, response),
      (error: ReportDatepickerError) => this.handleError(view, error)
    );
  }

  handleRequest(view: ReportDatepickerVacationView, from: string, to: string): void {
    if (view.type !== VacationTypes.PAID_LEAVE) {
      this.submitRequest(view);
      return;
    }
    view.isWaiting = true;
    view.wrongError = false;
    this.userService.getRequestOnProlong(from, to)
      .finally(() => view.isWaiting = false)
      .subscribe(
        (response: HttpResponse<number>) => this.processProlongation(view, response.body),
        () => view.wrongError = true
      );
  }

  prepareProlongation(view: ReportDatepickerVacationView, daysToAdd: number): void {
    view.prolongationDays = daysToAdd;
    const endDate = new Date(
      view.to.year, view.to.month - CALENDAR_MONTH_OFFSET, view.to.day);
    endDate.setDate(endDate.getDate() + daysToAdd);
    view.newEndDate = {
      year: endDate.getFullYear(), month: endDate.getMonth() + CALENDAR_MONTH_OFFSET,
      day: endDate.getDate()
    };
    view.showConfirmationScreen = true;
  }

  private prepareRequestParameters(
    view: ReportDatepickerVacationView, fromDate: Date
  ): VacationParams {
    const toDate = new Date(
      view.to.year, view.to.month - CALENDAR_MONTH_OFFSET, view.to.day);
    view.isWaiting = true;
    view.timeErr = false;
    return {
      from: moment(fromDate).format(DATE_FORMAT),
      to: moment(toDate).format(DATE_FORMAT),
      vacationType: VacationTypes[view.type],
      description: view.changeLog
    };
  }

  private processResponse(
    view: ReportDatepickerVacationView, httpResponse: HttpResponse<Object>
  ): void {
    const response = httpResponse.body as VacationResponse;
    view.successKey = VacationTypeCreation.vacation_created;
    view.successParams = this.successParameters(response);
    view.successMessage = true;
    this.ui.openPlanfix(response.idPf);
    this.ui.schedule(() => this.ui.close(MODAL_RESULT_OK), TIMEOUTS.LONG);
  }

  private successParameters(response: VacationResponse): SuccessParams {
    return {
      start: this.displayDate(response.start),
      finish: this.displayDate(response.finish),
      extension: response.countExtendDays > NO_VACATION_EXTENSION_DAYS
        ? this.translateService.instant(
          String(VacationTypeCreation.vacation_extention), {days: response.countExtendDays})
        : EMPTY_TEXT
    };
  }

  private displayDate(value: string): string {
    const parts = value.split(DATE_PART_SEPARATOR);
    return parts[DATE_DAY_PART_INDEX] + DISPLAY_DATE_SEPARATOR +
      parts[DATE_MONTH_PART_INDEX] + DISPLAY_DATE_SEPARATOR + parts[DATE_YEAR_PART_INDEX];
  }

  private processProlongation(
    view: ReportDatepickerVacationView, daysToAdd: number
  ): void {
    if (daysToAdd && daysToAdd) {
      this.prepareProlongation(view, daysToAdd);
    } else {
      this.submitRequest(view);
    }
  }

  private handleError(
    view: ReportDatepickerVacationView, error: ReportDatepickerError
  ): void {
    view.isWaiting = false;
    const body = error.error as ServerErrorBody;
    if (body && this.unpaidPreview.reject(view, body.errorCode)) { return; }
    if (error.status === HTTP_STATUS_CODES.CONFLICT) {
      this.handleConflict(view, error);
      return;
    }
    if (error.status === HTTP_STATUS_CODES.BAD_REQUEST) {
      this.handleBadRequest(view, error);
      return;
    }
    applyReportDatepickerError(view, error);
  }

  private handleConflict(view: ReportDatepickerVacationView, error: ReportDatepickerError): void {
    if (Array.isArray(error.error)) {
      view.currentVacations = error.error as CurrentVacation[];
      view.wrongError409 = true;
      return;
    }
    view.currentVacations = [];
    view.wrongError409 = false;
    view.wrongError = true;
  }

  private handleBadRequest(view: ReportDatepickerVacationView, error: ReportDatepickerError): void {
    const body = error.error as ServerErrorBody;
    if (body && !body.errorCode && typeof body.message === STRING_TYPE &&
        /^-?[0-9]+$/.test(body.message)) {
      this.applyVacationDays(view, body);
      return;
    }
    view.wrongError = true;
  }

  private applyVacationDays(
    view: ReportDatepickerVacationView, error: ServerErrorBody
  ): void {
    view.wrongError400 = true;
    const message = error && error.message || EMPTY_TEXT;
    view.vocationDays = Number(message.replace(VACATION_DAYS_SIGN, EMPTY_TEXT)) +
      VACATION_DAY_ADJUSTMENT;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/modals/report-datepicker-vacation.coordinator.ts